import { t } from '@lingui/core/macro';
import { useQueries } from '@tanstack/react-query';
import gql from 'graphql-tag';
import { useMemo } from 'react';

import { type OptionType } from '@/api/common';
import { fetcherSol } from '@/api/sol-fetcher';
import { getVariablesFromTableParamsNew } from '@/api/util/getVariablesFromTableParams';
import { InstantSearchSortOrder, type SolQueryParamsNew } from '@/components/InstantSearch';
import { CAMPAIGN_GROUP_MAX_LIMIT } from '@/constants';
import { useSolInfiniteQuery, useSolQuery } from '@/hooks/useSolQuery';

import {
  type CampaignActivityDataType,
  type CampaignActivityGQLResponse,
  type CampaignEfficiencyGQLResponse,
  type CampaignNameOptionsGQLResponse,
  type CampaignOpportunityActivityGQLResponse,
  type CampaignOptionGQLResponseType,
  type CampaignType,
  type CampaignsByIdGQLResponse,
} from './campaignType';

export const isCampaignDetailLimitMet = (campaignIds?: string[]) =>
  !!(campaignIds?.length && campaignIds.length <= CAMPAIGN_GROUP_MAX_LIMIT);

const replaceUnassignedName = (campaign: CampaignType, { includeVendorName = false } = {}) => {
  if (campaign?.id.includes('.unassigned__')) {
    return {
      ...campaign,
      name: includeVendorName
        ? t`Unassigned - ${campaign.vendor.name} - ${campaign.vendor.channel?.name ?? ''}`
        : t`Unassigned`,
    };
  }
  return campaign;
};

const campaignActivityQuery = gql`
  query CampaignActivity(
    $page: Pagination!
    $sort: [SortParamInput]!
    $filter: [FilterParamInput]!
    $startDate: DateTime!
    $endDate: DateTime!
    $audienceId: String
    $opportunityId: String
  ) {
    visualization {
      campaignActivity(
        page: $page
        sort: $sort
        filter: $filter
        startDate: $startDate
        endDate: $endDate
        audienceId: $audienceId
        opportunityId: $opportunityId
      ) {
        edges {
          node {
            ad {
              id
              name
              vendor {
                id
                name
                channel {
                  id
                  name
                }
              }
            }
            adClicks
            adImpressions
            allVisits: targetVisits
            allVisitsBest: visitsBest
            allVisitsWorst: visitsWorst
            baseAccounts
            botVisits
            closedWonFirstTouchAttribution
            closedWonInfluenced
            closedWonLinearAttribution
            companiesReached
            countCampaigns
            countClosedwonInfluenced
            countPipelineInfluenced
            engagedCompanies
            engagedCompaniesBest
            engagedCompaniesWorst
            fitScore
            fitScoreBest
            fitScoreWorst
            leadsInfluenced
            opportunitiesInfluenced
            opportunityInfluencedBest
            opportunityInfluencedWorst
            otherVisits
            percentOfEngagedCompanies
            percentOpportunitiesInfluenced
            pipelineFirstTouchAttribution
            pipelineInfluenced
            pipelineInfluencedBest
            pipelineInfluencedPerSpend
            pipelineInfluencedWorst
            pipelineLastTouchAttribution
            pipelineLinearAttribution
            spend
            spendPerTargetVisit
            spendPerVisitBest
            spendPerVisitWorst
            botViewThroughVisits
            otherViewThroughVisits
            targetViewThroughVisits
            targetVisits
            unresolvedViewThroughVisits
            unresolvedVisits
            viewThroughPercent
            viewThroughVisits
            visitEfficiency
          }
        }
        totalEdges
      }
    }
  }
`;

export const useCampaignActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
  opportunityId?: string,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<CampaignActivityGQLResponse>(
    {
      query: !!canExecuteQuery && campaignActivityQuery,
      variables: {
        ...getVariablesFromTableParamsNew(tableParams),
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
        opportunityId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  const campaignActivity = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return data.visualization.campaignActivity.edges.map(({ node }) => ({
      ...node,
      ad: replaceUnassignedName(node.ad),
    }));
  }, [data]);

  return {
    campaignActivity,
    totalResults: data?.visualization.campaignActivity.totalEdges,
    isLoading,
    error,
  };
};

type UseTopCampaignActivityByVendorIdsOptions = {
  vendorIds: string[];
  startDate?: string;
  endDate?: string;
  audienceId?: string;
  limit?: number;
};

export const useTopCampaignActivityByVendorIds = ({
  vendorIds,
  startDate,
  endDate,
  audienceId,
  limit = 5,
}: UseTopCampaignActivityByVendorIdsOptions) => {
  const uniqueVendorIds = useMemo(
    () => Array.from(new Set(vendorIds.filter((vendorId) => vendorId.length > 0))),
    [vendorIds],
  );
  const canExecuteQuery = startDate?.length && endDate?.length;

  const topCampaignQueries = useQueries({
    queries: uniqueVendorIds.map((vendorId) => ({
      queryKey: ['topCampaignActivityByVendor', vendorId, startDate, endDate, audienceId, limit],
      enabled: !!canExecuteQuery,
      queryFn: ({ signal }) => {
        if (!startDate || !endDate) {
          throw new Error('Top campaign activity query requires startDate and endDate.');
        }

        return fetcherSol<unknown, CampaignActivityGQLResponse>(
          {
            query: campaignActivityQuery,
            variables: {
              ...getVariablesFromTableParamsNew({
                page: {
                  offset: 0,
                  limit,
                },
                sort: {
                  field: 'visits',
                  direction: InstantSearchSortOrder.DESC,
                },
                filter: [
                  {
                    field: 'vendor.id',
                    operator: 'in',
                    operand: [vendorId],
                  },
                ],
              }),
              startDate: startDate + 'T00:00:00Z',
              endDate: endDate + 'T23:59:59Z',
              audienceId,
            },
          },
          { signal },
        );
      },
    })),
  });

  const campaignActivityByVendorId = useMemo(() => {
    return uniqueVendorIds.reduce<Record<string, CampaignActivityDataType[]>>(
      (campaignActivityMap, vendorId, index) => {
        const queryResult = topCampaignQueries[index];
        if (!queryResult?.data) {
          return campaignActivityMap;
        }

        const campaignActivityEdges = queryResult.data.visualization.campaignActivity.edges;
        campaignActivityMap[vendorId] = campaignActivityEdges.map(({ node }) => ({
          ...node,
          ad: replaceUnassignedName(node.ad),
        }));
        return campaignActivityMap;
      },
      {},
    );
  }, [topCampaignQueries, uniqueVendorIds]);

  const loadingVendorIds = useMemo(() => {
    return uniqueVendorIds.filter((_, index) => {
      const queryResult = topCampaignQueries[index];
      if (!queryResult) {
        return false;
      }

      return queryResult.isLoading || (queryResult.isFetching && queryResult.data == null);
    });
  }, [topCampaignQueries, uniqueVendorIds]);

  return {
    campaignActivityByVendorId,
    loadingVendorIds,
    isLoading: topCampaignQueries.some((queryResult) => queryResult.isLoading),
    isFetching: topCampaignQueries.some((queryResult) => queryResult.isFetching),
    error: topCampaignQueries.find((queryResult) => queryResult.error)?.error,
  };
};

export const useCampaignOpportunityActivity = (
  tableParams?: SolQueryParamsNew,
  startDate?: string,
  endDate?: string,
  audienceId?: string,
  opportunityId?: string,
) => {
  const canExecuteQuery = tableParams && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<CampaignOpportunityActivityGQLResponse>(
    {
      query:
        !!canExecuteQuery &&
        gql`
          query OpportunityCampaignActivity(
            $page: Pagination!
            $sort: [SortParamInput]!
            $filter: [FilterParamInput]!
            $startDate: DateTime!
            $endDate: DateTime!
            $opportunityId: String
          ) {
            visualization {
              opportunityCampaignActivity(
                page: $page
                sort: $sort
                filter: $filter
                startDate: $startDate
                endDate: $endDate
                opportunityId: $opportunityId
              ) {
                edges {
                  node {
                    ad {
                      id
                      name
                      vendor {
                        id
                        name
                        channel {
                          id
                          name
                        }
                      }
                    }
                    audienceVisits
                    closedWonLinearAttribution
                    closedWonInfluenced
                    closedWonFirstTouchAttribution
                    pipelineFirstTouchAttribution
                    pipelineInfluenced
                    pipelineLastTouchAttribution
                    pipelineLinearAttribution
                  }
                }
                totalEdges
              }
            }
          }
        `,
      variables: {
        ...getVariablesFromTableParamsNew(tableParams),
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        opportunityId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  return {
    campaignActivity: data?.visualization.opportunityCampaignActivity.edges.map(({ node }) => node),
    totalResults: data?.visualization.opportunityCampaignActivity.totalEdges,
    isLoading,
    error,
  };
};

export const useCampaignActivityFitScore = (
  campaignIds?: string[],
  startDate?: string,
  endDate?: string,
  audienceId?: string,
) => {
  const canExecuteQuery = campaignIds?.length && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<CampaignActivityGQLResponse>(
    {
      query: !!canExecuteQuery && campaignActivityQuery,
      variables: {
        page: {
          offset: 0,
          limit: 50,
        },
        sort: [
          {
            field: 'fitScore',
            direction: 'desc',
          },
        ],
        filter: [
          {
            field: 'id',
            operator: 'in',
            operand: campaignIds,
          },
        ],
        startDate: startDate + 'T00:00:00Z',
        endDate: endDate + 'T23:59:59Z',
        audienceId,
      },
    },
    {
      keepPreviousData: true,
    },
  );

  const fitScoreData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return data.visualization.campaignActivity.edges.map(({ node }) => ({
      ...node,
      ad: replaceUnassignedName(node.ad),
    }));
  }, [data]);

  return {
    fitScoreData,
    isLoading,
    error,
  };
};

export const useCampaignsByIds = (campaignIds?: string[]) => {
  const campaignSnippet = campaignIds
    ?.map(
      // eslint-disable-next-line lingui/no-unlocalized-strings
      (id, index) => `
    campaign${index + 1}: getById(id: $id${index + 1}) {
      id
      name
      vendor {
        id
        name
        channel {
          id
          name
          color
        }
      }
    }
  `,
    )
    .join('\n');

  // eslint-disable-next-line lingui/no-unlocalized-strings
  const query = `query GetCampaignsByIds(${campaignIds?.map((_, index) => `$id${index + 1}: String!`).join(', ')}) {
    campaigns {
      ${campaignSnippet}
    }
  }`;

  const { data, isLoading, error } = useSolQuery<CampaignsByIdGQLResponse>({
    query:
      !!isCampaignDetailLimitMet(campaignIds) &&
      gql`
        ${query}
      `,
    variables: campaignIds?.reduce<Record<string, string>>((acc, id, index) => {
      acc[`id${index + 1}`] = id;
      return acc;
    }, {}),
  });

  const campaigns = useMemo(
    () =>
      data &&
      Object.keys(data?.campaigns).map((key) => ({
        ...replaceUnassignedName(data?.campaigns[key]),
      })),
    [data],
  );

  return {
    campaigns,
    isLoading,
    error,
  };
};

export const useCampaignVisitEfficiency = (
  campaignIds?: string[],
  startDate?: string,
  endDate?: string,
  audienceIds?: string | string[],
) => {
  const canExecuteQuery = campaignIds?.length && startDate?.length && endDate?.length;
  const { data, isLoading, error } = useSolQuery<CampaignEfficiencyGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query CampaignEfficiency(
          $adIds: [String!]!
          $startDate: DateTime
          $endDate: DateTime
          $audienceIds: [String]
        ) {
          visualization {
            campaignEfficiency(
              adIds: $adIds
              startDate: $startDate
              endDate: $endDate
              audienceIds: $audienceIds
            ) {
              allVisits
              botVisits
              tamVisits
              audienceVisits
              otherNonAudienceVisits
              otherNonTamVisits
              unresolvedVisits
            }
          }
        }
      `,
    variables: {
      adIds: campaignIds,
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds,
    },
  });

  const efficiencyData = useMemo(() => {
    const campaignEfficiency = data?.visualization.campaignEfficiency[0];

    if (!campaignEfficiency) {
      return undefined;
    }

    return {
      allVisits: campaignEfficiency.allVisits,
      tamVisits: campaignEfficiency.tamVisits,
      audienceVisits: campaignEfficiency.audienceVisits,
      targetVisits: audienceIds ? campaignEfficiency.audienceVisits : campaignEfficiency.tamVisits,
      otherVisits: audienceIds
        ? campaignEfficiency.otherNonAudienceVisits
        : campaignEfficiency.otherNonTamVisits,
      botVisits: campaignEfficiency.botVisits,
      unresolvedVisits: campaignEfficiency.unresolvedVisits,
    };
  }, [audienceIds, data]);

  return {
    efficiencyData,
    isLoading,
    error,
  };
};

export const useCampaignNameOptions = ({
  search = '',
  pageSize = 50,
}: {
  search?: string;
  pageSize?: number;
}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<CampaignNameOptionsGQLResponse>({
      queryKey: ['campaignNameOptions', search],
      queryFn: ({ pageParam = 0, signal }) => {
        return {
          query: gql`
            query GetCampaignNameOptions(
              $page: Pagination!
              $sort: [SortParamInput]!
              $filter: [FilterParamInput]!
            ) {
              campaigns {
                get(page: $page, sort: $sort, filter: $filter) {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                  pageMeta {
                    hasNext
                  }
                }
              }
            }
          `,
          variables: {
            ...getVariablesFromTableParamsNew({
              page: {
                offset: pageParam * pageSize,
                limit: pageSize,
              },
              sort: {
                direction: InstantSearchSortOrder.ASC,
                field: 'name',
              },
              filter: search ? [{ field: 'name', operator: 'iLike', operand: `%${search}%` }] : [],
            }),
          },
          signal,
        };
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.campaigns.get.pageMeta.hasNext ? pages.length + 1 : undefined;
      },
    });

  const hasMore = data?.pages?.[data.pages.length - 1].campaigns.get.pageMeta.hasNext;

  const campaignOptions = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.pages.map((d) => d.campaigns.get.edges.map((edge) => edge.node)).flat();
  }, [data]);

  return {
    campaignOptions,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};

export const useCampaignOptions = ({
  search = '',
  channelId,
  pageSize = 50,
  prependedOptions = [],
  startDate,
  endDate,
}: {
  search?: string;
  channelId?: string;
  pageSize?: number;
  prependedOptions?: OptionType[];
  startDate?: string;
  endDate?: string;
}) => {
  const { data, isLoading, isFetchingNextPage, error, fetchNextPage } =
    useSolInfiniteQuery<CampaignOptionGQLResponseType>({
      queryKey: ['campaignOptions', search, startDate, endDate],
      queryFn: ({ pageParam = 0, signal }) => {
        const canExecuteQuery = startDate && endDate;

        return {
          query:
            !!canExecuteQuery &&
            gql`
              query GetCampaignOptions(
                $page: Pagination!
                $sort: [SortParamInput]!
                $filter: [FilterParamInput]!
                $endDate: DateTime!
                $startDate: DateTime!
              ) {
                visualization {
                  campaignActivity(
                    page: $page
                    sort: $sort
                    filter: $filter
                    endDate: $endDate
                    startDate: $startDate
                  ) {
                    edges {
                      node {
                        ad {
                          id
                          name
                          vendor {
                            id
                            name
                            channel {
                              id
                              name
                            }
                          }
                        }
                      }
                    }
                    pageMeta {
                      hasNext
                    }
                  }
                }
              }
            `,
          variables: {
            ...getVariablesFromTableParamsNew({
              page: {
                offset: pageParam * pageSize,
                limit: pageSize,
              },
              sort: {
                direction: InstantSearchSortOrder.ASC,
                field: 'campaign.name',
              },
              filter: [
                ...(search
                  ? [{ field: 'campaign.name', operator: 'iLike' as const, operand: `%${search}%` }]
                  : []),
                ...(channelId
                  ? [{ field: 'channel.id', operator: 'eq' as const, operand: channelId }]
                  : []),
              ],
            }),
            startDate: startDate + 'T00:00:00Z',
            endDate: endDate + 'T23:59:59Z',
          },
          signal,
        };
      },
      getNextPageParam: (lastPage, pages) => {
        return lastPage.visualization.campaignActivity.pageMeta.hasNext
          ? pages.length + 1
          : undefined;
      },
    });

  const hasMore =
    data?.pages?.[data.pages.length - 1].visualization.campaignActivity.pageMeta.hasNext;

  const campaignOptions = useMemo(() => {
    if (!data) {
      return prependedOptions;
    }

    return (prependedOptions || []).concat(
      data.pages
        .map((d) =>
          d.visualization.campaignActivity.edges.map((edge) =>
            replaceUnassignedName(edge.node.ad, { includeVendorName: true }),
          ),
        )
        .flat(),
    );
  }, [data, prependedOptions]);

  return {
    campaignOptions,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
  };
};
